import React, { useState } from 'react';
import { Alert, Card, Descriptions, Space, Switch, Tag, Timeline, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, FieldTimeOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { useCronControl, useCronHistory, setCronEnabled } from '../../../models/useCronControls';
import {
  KNOWN_CRONS, deriveCronHealth, effectiveEnabled, formatTs,
} from '../../../utils/cronControls';

const { Text, Title } = Typography;

const HEALTH_TAGS = {
  unknown: { color: undefined, label: 'NO TICKS YET' },
  healthy: { color: 'green', label: 'HEALTHY' },
  overdue: { color: 'volcano', label: 'OVERDUE' },
  failing: { color: 'red', label: 'FAILING' },
};

/**
 * The drill-in status page for ONE cron: /admin/ops/crons/:cronName.
 * Everything on it comes from two model hooks (control doc + history, both
 * live) and the pure helpers — this screen renders and wires, nothing else.
 */
export function AdminCronDetailScreen({ match }) {
  const cronName = match && match.params && match.params.cronName;
  const [loading, control, loadError] = useCronControl(cronName);
  const [historyLoading, history, historyError] = useCronHistory(cronName);
  const [toggleError, setToggleError] = useState(null);
  const [toggling, setToggling] = useState(false);

  const catalogEntry = KNOWN_CRONS.find((c) => c.name === cronName) || null;
  const cron = control || catalogEntry;

  const onToggle = async (enabled) => {
    setToggling(true);
    setToggleError(null);
    try {
      await setCronEnabled(cronName, enabled);
      // No local state write — the subscription delivers the new truth.
    } catch (err) {
      setToggleError(err.message || `Could not switch ${cronName}`);
    } finally {
      setToggling(false);
    }
  };

  if (!loading && !cron) {
    return (
      <Card style={{ textAlign: 'left' }} data-testid="cron-detail-unknown">
        <Space direction="vertical">
          <Title level={4}>Unknown cron</Title>
          <Text type="secondary">
            No control record or catalog entry for <Text code>{cronName}</Text>.
            Crons appear here after their first tick, or when they are switched.
          </Text>
          <Link to="/admin/ops/crons">← All crons</Link>
        </Space>
      </Card>
    );
  }
  if (!cron) return null; // first render while the doc loads

  const on = effectiveEnabled(cron);
  const health = deriveCronHealth(cron, moment().valueOf());
  const healthTag = HEALTH_TAGS[health.status] || HEALTH_TAGS.unknown;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', textAlign: 'left' }} data-testid="cron-detail-screen">
      <Card>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space align="center" wrap size={16}>
            <Title level={3} style={{ margin: 0 }}><FieldTimeOutlined /> {cron.name}</Title>
            <Tag
              color={healthTag.color}
              style={{ fontSize: 16, padding: '4px 12px' }}
              data-testid={`cron-detail-health-${health.status}`}
            >
              {healthTag.label}
            </Tag>
            <Space>
              <Switch checked={on} loading={toggling} onChange={onToggle} data-testid="cron-detail-switch" />
              {on
                ? <Tag color="green" data-testid="cron-detail-on">ON</Tag>
                : <Tag color="red" data-testid="cron-detail-off">OFF</Tag>}
              {cron.enabled == null && <Tag data-testid="cron-detail-default">default</Tag>}
            </Space>
          </Space>
          {cron.description && <Text type="secondary">{cron.description}</Text>}
          {health.status === 'overdue' && (
            <Alert
              type="warning"
              showIcon
              message={`Silent since ${moment(health.sinceMs).format('YYYY-MM-DD HH:mm')} — past its ${cron.overdueAfterMinutes} minute tolerance.`}
              description="Heartbeats fire even while a cron is OFF, so silence means the schedule itself is broken (deleted scheduler job, failed deploy) — not a paused cron."
              style={{ borderRadius: 8 }}
              data-testid="cron-detail-overdue-alert"
            />
          )}
          {health.status === 'failing' && (
            <Alert
              type="error"
              showIcon
              message="The last run failed."
              description={health.lastError || 'See Cloud Logging for the full execution log.'}
              style={{ borderRadius: 8 }}
              data-testid="cron-detail-failing-alert"
            />
          )}
          {toggleError && (
            <Alert
              type="error"
              showIcon
              closable
              onClose={() => setToggleError(null)}
              message={toggleError}
              style={{ borderRadius: 8 }}
              data-testid="cron-detail-toggle-error"
            />
          )}
          {loadError && <Alert type="error" showIcon message={loadError} style={{ borderRadius: 8 }} />}
          <Descriptions size="small" bordered column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="Schedule">{cron.schedule || '—'}</Descriptions.Item>
            <Descriptions.Item label="Default">{cron.defaultEnabled ? 'ON' : 'OFF'}</Descriptions.Item>
            <Descriptions.Item label="Overdue after">
              {cron.overdueAfterMinutes ? `${cron.overdueAfterMinutes} min` : 'not declared'}
            </Descriptions.Item>
            <Descriptions.Item label="Created">{formatTs(cron.createdAt) || '—'}</Descriptions.Item>
            <Descriptions.Item label="Last run">{formatTs(cron.lastRunAt) || 'never'}</Descriptions.Item>
            <Descriptions.Item label="Last skipped">{formatTs(cron.lastSkippedAt) || 'never'}</Descriptions.Item>
            <Descriptions.Item label="Last success">{formatTs(cron.lastSuccessAt) || 'never'}</Descriptions.Item>
            <Descriptions.Item label="Last error">{formatTs(cron.lastErrorAt) || 'never'}</Descriptions.Item>
            <Descriptions.Item label="Last changed">
              {cron.updatedBy
                ? (
                  <>
                    <a href={`/admin/users/${cron.updatedBy}`}>{cron.updatedByName || cron.updatedBy}</a>
                    {formatTs(cron.updatedAt) ? ` · ${formatTs(cron.updatedAt)}` : ''}
                  </>
                )
                : 'never — running on its default'}
            </Descriptions.Item>
          </Descriptions>
        </Space>
      </Card>

      <Card title={<Text strong>Switch history</Text>} data-testid="cron-detail-history">
        {historyError && <Alert type="error" showIcon message={historyError} style={{ borderRadius: 8 }} />}
        {!historyLoading && !historyError && history.length === 0 && (
          <Text type="secondary">No switches yet — this cron has only ever run on its default.</Text>
        )}
        {history.length > 0 && (
          <Timeline style={{ marginTop: 8 }}>
            {history.map((entry) => (
              <Timeline.Item
                key={entry.id}
                dot={entry.enabled
                  ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  : <CloseCircleOutlined style={{ color: '#f5222d' }} />}
              >
                <Space direction="vertical" size={0}>
                  <Text strong>{entry.enabled ? 'Switched ON' : 'Switched OFF'}</Text>
                  <Text type="secondary">
                    by <a href={`/admin/users/${entry.byUid}`}>{entry.byName || entry.byUid}</a>
                    {formatTs(entry.at) ? ` · ${formatTs(entry.at)}` : ''}
                  </Text>
                </Space>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Card>

      <Link to="/admin/ops/crons">← All crons</Link>
    </Space>
  );
}

export default AdminCronDetailScreen;
